import React from "react";
import { makeAutoObservable, runInAction } from "mobx";
import sha256 from "crypto-js/sha256.js";

class AuthStore {
  passwordHash = null;
  disabled = false;
  isAuthenticated = false;
  loading = true;

  logoutTimer = null;
  inactivityTimer = null;
  resetActivityHandler = null;

  // === MASTER AUTH TOGGLE ===
  enableAuth = false;

  // === CONFIGURATION FLAGS ===
  enablePersistence = false;           // (A) Persist login (one-time only) beyond browser session
  enableSessionCloseLogout = false;    // (B1) Logout when browser/tab closes
  enableSessionMaxTimeout = true;      // (B2) Logout after max lifetime
  enableInactivityTimeout = true;      // (B3) Logout after inactivity

  // === TIMEOUTS (in minutes) ===
  sessionMaxTimeoutMinutes = 60;
  inactivityTimeoutMinutes = 10;

  // sessionMaxTimeoutMinutes = 2;
  // inactivityTimeoutMinutes = 1;


  constructor() {
    makeAutoObservable(this);

    if (!this.enableAuth) {
      this.isAuthenticated = true;
      this.loading = false;
      console.info("[AuthStore] Authentication disabled — auto-logged in");
      return;
    }

    this.loadPasswordHash();

    // Decide storage type based on flags
    if (this.enablePersistence) {
      this.storage = localStorage;
    } else if (this.enableSessionCloseLogout) {
      this.storage = sessionStorage;
    } else {
      this.storage = localStorage; // survives close, still respects expiry
    }

    const auth = this.storage.getItem("auth");
    const expiry = this.storage.getItem("authExpiry");

    if (auth === "true") {
      const stillValid =
        this.enablePersistence ||
        (this.enableSessionMaxTimeout && expiry && Date.now() < parseInt(expiry, 10));

      if (stillValid) {
        this.isAuthenticated = true;

        if (!this.enablePersistence) {
          if (this.enableSessionMaxTimeout) this.startLogoutTimer();
          if (this.enableInactivityTimeout) {
            this.startInactivityTimer();
            this.addActivityListeners();
          }
        }
      } else {
        this.clearSession();
      }
    }
  }

  async loadPasswordHash() {
    if (!this.enableAuth) return;

    try {
      const res = await fetch("/password.json?_t=" + Date.now());
      const data = await res.json();

      if (data.disabled) {
        runInAction(() => {
          this.disabled = true;
          this.isAuthenticated = true;
          this.loading = false;
        });
        return;
      }

      const decodedBytes = atob(data.hash);
      let hex = "";
      for (let i = 0; i < decodedBytes.length; i++) {
        hex += ("0" + decodedBytes.charCodeAt(i).toString(16)).slice(-2);
      }

      runInAction(() => {
        this.passwordHash = hex;
        this.disabled = false;
        this.loading = false;
      });
    } catch (e) {
      console.error("Failed to load password.json", e);
      runInAction(() => (this.loading = false));
    }
  }

  login(password) {
    if (!this.enableAuth || this.disabled) {
      this.isAuthenticated = true;
      return true;
    }

    const hash = sha256(password).toString();
    if (hash === this.passwordHash) {
      this.isAuthenticated = true;

      if (this.enablePersistence) {
        this.storage = localStorage;
      } else if (this.enableSessionCloseLogout) {
        this.storage = sessionStorage;
      } else {
        this.storage = localStorage;
      }

      this.storage.setItem("auth", "true");

      if (!this.enablePersistence) {
        if (this.enableSessionMaxTimeout) {
          const expiryTime = Date.now() + this.sessionMaxTimeoutMinutes * 60 * 1000;
          this.storage.setItem("authExpiry", expiryTime.toString());
          this.startLogoutTimer();
        }
        if (this.enableInactivityTimeout) {
          this.startInactivityTimer();
          this.addActivityListeners();
        }
      }

      return true;
    }

    return false;
  }

  startLogoutTimer() {
    this.clearLogoutTimer();

    const expiry = this.storage.getItem("authExpiry");
    if (!expiry) return;

    const expiryTime = parseInt(expiry, 10);
    const now = Date.now();
    let timeLeft = expiryTime - now;

    if (timeLeft <= 0) {
      console.warn("[AuthStore] Session already expired — logging out");
      this.logout();
      return;
    }

    const checkIntervalMs = 30 * 1000;

    const checkExpiry = () => {
      const now = Date.now();
      if (now >= expiryTime) {
        console.warn("[AuthStore] Session max lifetime reached — logging out");
        this.logout();
      } else {
        timeLeft = expiryTime - now;
        this.logoutTimer = setTimeout(checkExpiry, Math.min(checkIntervalMs, timeLeft));
      }
    };

    this.logoutTimer = setTimeout(checkExpiry, Math.min(checkIntervalMs, timeLeft));
  }

  startInactivityTimer() {
    this.clearInactivityTimer();
    this.inactivityTimer = setTimeout(() => {
      console.warn("[AuthStore] User inactive — logging out");
      this.logout();
    }, this.inactivityTimeoutMinutes * 60 * 1000);
  }

  resetInactivityTimer() {
    if (!this.isAuthenticated || !this.enableInactivityTimeout) return;
    this.clearInactivityTimer();
    this.startInactivityTimer();
  }

  addActivityListeners() {
    if (this.activityListenersAdded) return;
    this.activityListenersAdded = true;
    this.resetActivityHandler = () => this.resetInactivityTimer();

    window.addEventListener("mousemove", this.resetActivityHandler);
    window.addEventListener("keydown", this.resetActivityHandler);
    window.addEventListener("mousedown", this.resetActivityHandler);
    window.addEventListener("touchstart", this.resetActivityHandler);
  }

  removeActivityListeners() {
    if (!this.activityListenersAdded) return;
    this.activityListenersAdded = false;
    if (!this.resetActivityHandler) return;

    window.removeEventListener("mousemove", this.resetActivityHandler);
    window.removeEventListener("keydown", this.resetActivityHandler);
    window.removeEventListener("mousedown", this.resetActivityHandler);
    window.removeEventListener("touchstart", this.resetActivityHandler);

    this.resetActivityHandler = null;
  }

  clearLogoutTimer() {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  }

  clearInactivityTimer() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  clearSession() {
    this.isAuthenticated = false;
    this.clearLogoutTimer();
    this.clearInactivityTimer();
    this.removeActivityListeners();

    if (this.storage) {
      this.storage.removeItem("auth");
      this.storage.removeItem("authExpiry");
    }
  }

  logout() {
    console.info("[AuthStore] Logging out");
    this.clearSession();
  }
}

const authStore = new AuthStore();
export default authStore;
